import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function SMTPAdminPage() {
  const [settings, setSettings] = useState(null);
  const [testEmail, setTestEmail] = useState("");

  const load = () => api.get("/settings").then((r) => setSettings(r.data));
  useEffect(() => { load(); }, []);

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  const setSmtp = (patch) => setSettings((s) => ({ ...s, smtp: { ...s.smtp, ...patch } }));
  const save = async () => {
    const smtp = settings.smtp || {};
    const password = smtp.password || "";
    const host = (smtp.host || "").trim();

    if (host.includes("@") || /\s/.test(host)) {
      return toast.error("SMTP host must be a mail server name such as smtp.gmail.com, not an email address");
    }

    if (smtp.enabled && !host) {
      return toast.error("SMTP host is required");
    }

    if (smtp.enabled && !(smtp.username || "").trim()) {
      return toast.error("SMTP username is required");
    }

    // A blank password keeps the saved one.
    if (smtp.enabled && !password && !smtp.password_set) {
      return toast.error("SMTP password is required");
    }

    if (password && password.length < 8) {
      return toast.error("SMTP password must contain at least 8 characters");
    }

    if (password && password.length > 16) {
      return toast.error("SMTP password must not exceed 16 characters");
    }

    try {
      const { data } = await api.put("/settings/smtp", smtp);
      setSettings(data);
      toast.success("SMTP settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const test = async () => {
    if (!testEmail) return toast.error("Provide a recipient");
    try {
      const { data } = await api.post("/settings/test-email", { to: testEmail });
      if (data.status === "sent") toast.success("Test email sent");
      else toast.error(`Test email ${data.status}: ${data.error || "unknown error"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const smtp = settings.smtp || {};

  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-smtp-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">System</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">SMTP email</h1>
        <p className="text-sm text-muted-foreground">Configure outbound email delivery. When disabled or unconfigured, in-app notifications remain fully operational.</p>
      </div>

      <div className="rounded-xl border border-border bg-card/70 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <Label>Enable SMTP delivery</Label>
          <Switch data-testid="smtp-enabled" checked={!!smtp.enabled} onCheckedChange={(v) => setSmtp({ enabled: v })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>Host</Label><Input data-testid="smtp-host" value={smtp.host || ""} onChange={(e) => setSmtp({ host: e.target.value })} placeholder="smtp.gmail.com" /></div>
          <div><Label>Port</Label><Input data-testid="smtp-port" type="number" value={smtp.port || 587} onChange={(e) => setSmtp({ port: Number(e.target.value) })} /></div>
          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center gap-2"><Switch data-testid="smtp-tls" checked={!!smtp.use_tls} onCheckedChange={(v) => setSmtp({ use_tls: v })} /><span className="text-xs">STARTTLS</span></div>
            <div className="flex items-center gap-2"><Switch data-testid="smtp-ssl" checked={!!smtp.use_ssl} onCheckedChange={(v) => setSmtp({ use_ssl: v })} /><span className="text-xs">SSL</span></div>
          </div>
          <div><Label>Username</Label><Input data-testid="smtp-username" value={smtp.username || ""} onChange={(e) => setSmtp({ username: e.target.value })} /></div>
          <div>
            <Label>Password</Label>
            <Input
              data-testid="smtp-password"
              type="password"
              minLength={8}
              maxLength={16}
              value={smtp.password || ""}
              onChange={(e) => setSmtp({ password: e.target.value })}
              placeholder={smtp.password_set ? "Saved — leave blank to keep" : "8 to 16 characters"}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Password must contain 8–16 characters. For Gmail use a 16-character App Password without spaces.
            </p>
          </div>
          <div><Label>From email</Label><Input data-testid="smtp-from-email" value={smtp.from_email || ""} onChange={(e) => setSmtp({ from_email: e.target.value })} /></div>
          <div><Label>From name</Label><Input data-testid="smtp-from-name" value={smtp.from_name || ""} onChange={(e) => setSmtp({ from_name: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button data-testid="smtp-save" onClick={save} className="bg-[#d4af37] text-black hover:bg-[#e5c158]">Save</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card/70 p-5 space-y-3">
        <Label>Send test email</Label>
        <div className="flex gap-2">
          <Input data-testid="smtp-test-to" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" />
          <Button data-testid="smtp-test" variant="outline" onClick={test}>Send test</Button>
        </div>
      </div>
    </div>
  );
}
